import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { STICKY_PAGE_RAIL_CLASS } from './stickyPageRail';
import { acquireBodyScrollLock } from '../../utils/bodyScrollLock';
import { SectionRailMobileTrigger } from './SectionRailMobileTrigger';

export type FilterSectionProps = {
  id: string;
  label: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
};

/** Collapsible group matching Ops / Admin sidebar section chrome. */
export const FilterSection: React.FC<FilterSectionProps> = ({
  id,
  label,
  defaultOpen = true,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        type="button"
        className="flex w-full min-h-[44px] items-center justify-between px-2 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-300 cursor-pointer"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={`filter-section-${id}`}
      >
        <span>{label}</span>
        <span aria-hidden className="text-gray-600">
          {open ? '−' : '+'}
        </span>
      </button>
      {open ? (
        <div id={`filter-section-${id}`} className="mt-2 space-y-3 px-0.5">
          {children}
        </div>
      ) : null}
    </div>
  );
};

export type SectionRailAnchor = {
  id: string;
  label: string;
  description?: string;
};

export type SectionRailGroup = {
  id: string;
  label: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  defaultOpen?: boolean;
};

interface DirectoryBrowseLayoutProps {
  /** Page title in the rail header (Organizer / Judge shell style). */
  title: string;
  /** Short support line under the title. */
  description?: string;
  mobileTriggerLabel?: string;
  /** In-page section jump links with scroll spy. */
  sections?: SectionRailAnchor[];
  /** Collapsible rail groups (filters, quick actions, garage, etc.). */
  groups?: SectionRailGroup[];
  /** Badge on the mobile trigger when filters are active. */
  activeFilterCount?: number;
  children: React.ReactNode;
  onMobileClose?: () => void;
  mobileOpen?: boolean;
  onMobileOpenChange?: (open: boolean) => void;
}

const SCROLL_SPY_OFFSET_PX = 96;

function scrollToSection(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - SCROLL_SPY_OFFSET_PX;
  window.scrollTo({ top, behavior: 'smooth' });
}

/**
 * Ops-style sticky sidebar for browse destinations.
 * Title + collapsible groups mirror Organizer / Judge workspace chrome.
 * Desktop rail sticks while main content scrolls (same recipe as build Owner tools).
 */
export const DirectoryBrowseLayout: React.FC<DirectoryBrowseLayoutProps> = ({
  title,
  description,
  mobileTriggerLabel,
  sections = [],
  groups = [],
  activeFilterCount = 0,
  children,
  onMobileClose,
  mobileOpen: controlledOpen,
  onMobileOpenChange,
}) => {
  const reactId = useId();
  const navId = `section-rail-${reactId.replace(/:/g, '')}`;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const mobileOpen = isControlled ? controlledOpen : uncontrolledOpen;
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [sectionsOpen, setSectionsOpen] = useState(true);

  const sectionsKey = sections.map((s) => `${s.id}:${s.label}`).join('|');
  const visibleSections = useMemo(
    () => sections.filter((s) => s.id && s.label),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by sectionsKey
    [sectionsKey]
  );

  const [activeSectionId, setActiveSectionId] = useState(visibleSections[0]?.id || '');

  useEffect(() => {
    if (!visibleSections.some((s) => s.id === activeSectionId) && visibleSections[0]) {
      setActiveSectionId(visibleSections[0].id);
    }
  }, [activeSectionId, visibleSections]);

  const resolveActiveSection = useCallback(() => {
    if (visibleSections.length === 0) return;
    let current = visibleSections[0].id;
    for (const item of visibleSections) {
      const el = document.getElementById(item.id);
      if (!el) continue;
      const top = el.getBoundingClientRect().top;
      if (top <= SCROLL_SPY_OFFSET_PX) {
        current = item.id;
      }
    }
    setActiveSectionId((prev) => (prev === current ? prev : current));
  }, [visibleSections]);

  useEffect(() => {
    if (visibleSections.length === 0) return undefined;
    let frame = 0;
    const onScrollOrResize = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(resolveActiveSection);
    };
    resolveActiveSection();
    const settle = window.setTimeout(resolveActiveSection, 120);
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.clearTimeout(settle);
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [sectionsKey, resolveActiveSection, visibleSections.length]);

  const setMobileOpen = (open: boolean) => {
    if (!isControlled) setUncontrolledOpen(open);
    onMobileOpenChange?.(open);
    if (!open) onMobileClose?.();
  };

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const releaseScrollLock = acquireBodyScrollLock();
    return () => {
      document.removeEventListener('keydown', onKey);
      releaseScrollLock();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- close-on-escape only
  }, [mobileOpen]);

  const activeSectionLabel = visibleSections.find((s) => s.id === activeSectionId)?.label;
  const triggerLabel = mobileTriggerLabel || title;
  const showRail = visibleSections.length > 0 || groups.length > 0;

  const sectionLinks = visibleSections.length > 0 ? (
    <div>
      <button
        type="button"
        className="flex w-full min-h-[44px] items-center justify-between px-2 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-300 cursor-pointer"
        onClick={() => setSectionsOpen((v) => !v)}
        aria-expanded={sectionsOpen}
      >
        <span>Sections</span>
        <span aria-hidden className="text-gray-600">
          {sectionsOpen ? '−' : '+'}
        </span>
      </button>
      {sectionsOpen ? (
        <ul className="mt-1 space-y-0.5 list-none">
          {visibleSections.map((item) => {
            const active = item.id === activeSectionId;
            return (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    setMobileOpen(false);
                    scrollToSection(item.id);
                  }}
                  className={`flex flex-col justify-center px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 min-h-[44px] cursor-pointer border ${
                    active
                      ? 'bg-orange-500/[0.1] text-orange-300 border-orange-500/30'
                      : 'text-gray-300 border-transparent hover:bg-white/[0.04] hover:text-white'
                  }`}
                  aria-current={active ? 'location' : undefined}
                >
                  <span className="truncate">{item.label}</span>
                  {item.description ? (
                    <span className="text-[11px] font-normal text-gray-500 mt-0.5 leading-snug">
                      {item.description}
                    </span>
                  ) : null}
                </a>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  ) : null;

  const groupBlocks = groups.map((group) => {
    const isCollapsed = collapsedGroups[group.id] ?? group.defaultOpen === false;
    return (
      <div key={group.id}>
        <button
          type="button"
          className="flex w-full min-h-[44px] items-center justify-between px-2 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-300 cursor-pointer"
          onClick={() => setCollapsedGroups((c) => ({ ...c, [group.id]: !isCollapsed }))}
          aria-expanded={!isCollapsed}
        >
          <span>{group.label}</span>
          <span aria-hidden className="text-gray-600">
            {isCollapsed ? '+' : '−'}
          </span>
        </button>
        {!isCollapsed ? (
          <div className="mt-2 px-0.5 space-y-3">
            {group.children}
            {group.footer ? <div className="pt-1">{group.footer}</div> : null}
          </div>
        ) : null}
      </div>
    );
  });

  const panel = (
    <div className="flex flex-col h-full min-h-0 bg-[#0c0c0c]">
      <div className="flex items-start justify-between gap-2 px-4 py-4 border-b border-white/[0.06] shrink-0">
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-lg font-semibold tracking-tight text-white">{title}</h2>
          {description ? (
            <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">{description}</p>
          ) : null}
          {activeSectionLabel ? (
            <p className="text-[11px] text-gray-500 mt-1 leading-snug">Viewing {activeSectionLabel}</p>
          ) : null}
        </div>
        <button
          type="button"
          className="lg:hidden p-2 min-h-[44px] min-w-[44px] text-gray-400 hover:text-white cursor-pointer rounded-lg shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/25"
          aria-label="Close sidebar"
          onClick={() => setMobileOpen(false)}
        >
          <XMarkIcon className="w-5 h-5" aria-hidden />
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 py-4 space-y-4">
        {sectionLinks}
        {groupBlocks}
      </div>
    </div>
  );

  const mobileDrawer =
    mobileOpen && typeof document !== 'undefined'
      ? createPortal(
          <>
            <button
              type="button"
              className="fixed inset-0 z-[80] bg-black/55 lg:hidden cursor-pointer"
              aria-label="Close sidebar overlay"
              onClick={() => setMobileOpen(false)}
            />
            <aside
              id={navId}
              className="duo-safe-drawer fixed inset-y-0 left-0 z-[90] flex flex-col w-[min(20rem,88vw)] max-w-full overflow-hidden shadow-2xl shadow-black/50 lg:hidden border-r border-white/[0.06]"
              aria-label={title}
              role="dialog"
              aria-modal="true"
            >
              {panel}
            </aside>
          </>,
          document.body
        )
      : null;

  if (!showRail) {
    return <div className="w-full">{children}</div>;
  }

  return (
    <div className="w-full">
      <div className="lg:hidden mb-4 section-rail-sticky sticky top-16 z-30 -mx-1 px-1 py-2 backdrop-blur-md bg-[#0a0a0a]/90">
        <SectionRailMobileTrigger
          title={triggerLabel}
          subtitle={activeSectionLabel || null}
          badgeCount={activeFilterCount}
          expanded={mobileOpen}
          controlsId={navId}
          onClick={() => setMobileOpen(true)}
        />
      </div>

      <div className="flex gap-0 lg:gap-8 items-start">
        <aside
          className={`${STICKY_PAGE_RAIL_CLASS} border border-white/[0.06]`}
          aria-label={title}
        >
          {panel}
        </aside>

        {mobileDrawer}

        <div className="flex-1 min-w-0 bg-[#0a0a0a]">{children}</div>
      </div>
    </div>
  );
};

export default DirectoryBrowseLayout;
